// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { Box, Typography } from "@outschool/backpack";
import {
  ClassDetailsQuery_ActivityFragment,
  ListingCard_ListingFragment,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { createPathWithActivity } from "@outschool/routes";
import { isUuid } from "@outschool/text";
import { useProductClicked } from "@outschool/ui-analytics";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  Loading,
  PageWidthSection,
} from "@outschool/ui-legacy-component-library";
import { Screen, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { MiniListing as Listing, SIMILARITY_ALGORITHMS } from "../Listings";

type SimilarListingsMiniTilesProps = {
  activity: Pick<ClassDetailsQuery_ActivityFragment, "isPublished" | "uid">;
  listings?: Array<ListingCard_ListingFragment>;
  isFullPage?: boolean;
};

export default function SimilarListingsMiniTiles({
  activity,
  listings,
  isFullPage,
}: SimilarListingsMiniTilesProps) {
  const algorithm = !!activity.isPublished ? "tfidf" : "popularElasticsearch";
  const { query } = SIMILARITY_ALGORITHMS[algorithm];
  const [fetchedListings, setFetchedListings] = React.useState([]);
  const [miniTilesSectionExpanded, setMiniTilesSectionExpanded] =
    React.useState(false);
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\SimilarListingsMiniTiles"
  );

  const hasListingsSupplied = !!listings && listings?.length > 0;
  const { data, loading } = useQueryWithPreviousData(query, {
    skip: !activity || !isUuid(activity.uid) || hasListingsSupplied,
    variables: {
      activityUid: activity && activity.uid,
      skipRating: false,
      withRecentReviews: false,
      limit: 12,
    },
    context: { doNotRetry: true, batchKey: "similarListings" },
  });
  React.useEffect(() => {
    const fetchedListings = data?.listing?.similarListings[algorithm];
    if (hasListingsSupplied || loading) {
      return;
    }
    setFetchedListings(fetchedListings ?? []);
  }, [
    activity,
    algorithm,
    data?.listing?.similarListings,
    hasListingsSupplied,
    loading,
    query,
  ]);

  const similarListings = hasListingsSupplied ? listings : fetchedListings;
  if (!loading && (!similarListings || !Boolean(similarListings.length))) {
    return null;
  }

  const title = !!activity.isPublished
    ? t`There are no open spots for this class, but we found something similar!`
    : t`This class is no longer available, but we found something similar!`;
  const trackingLabel = !!activity.isPublished
    ? "show_similar_classes_above"
    : "previously_published_show_similar_classes";
  const isPublishedDesktop = !!activity.isPublished && !isMobile;
  const publishedDesktopLength = 6;
  const unpublishedMobileLength = 3;
  const tileListings = miniTilesSectionExpanded
    ? similarListings
    : similarListings?.slice(
        0,
        !isMobile ? publishedDesktopLength : unpublishedMobileLength
      );

  return (
    <PageWidthSection
      sx={{
        backgroundColor: isFullPage ? undefined : "#f8f8f9",
        paddingY: "32px",
        minHeight: 282,
      }}
    >
      <Box
        flex
        sx={{
          marginBottom: "20px",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant={!isMobile ? "h4" : "h6"}
          sx={{
            fontFamily: "'Ginto Normal',sans-serif",
            fontWeight: "600",
          }}
        >
          {title}
        </Typography>
        {isPublishedDesktop &&
          similarListings &&
          similarListings.length > publishedDesktopLength && (
            <TrackedButton
              variant="link"
              onClick={() =>
                setMiniTilesSectionExpanded(!miniTilesSectionExpanded)
              }
              sx={{
                fontSize: "16px",
                fontWeight: "500",
                color: "primary.500",
              }}
              trackingName={trackingLabel}
              trackingUniqueId={
                miniTilesSectionExpanded ? "show_less" : "show_more"
              }
            >
              {miniTilesSectionExpanded ? t("Show less") : t("Show more")}
            </TrackedButton>
          )}
      </Box>
      {!isPublishedDesktop &&
        similarListings &&
        similarListings.length > unpublishedMobileLength && (
          <Box
            flex
            sx={{
              marginBottom: "20px",
              justifyContent: "center",
            }}
          >
            <TrackedButton
              variant="link"
              onClick={() =>
                setMiniTilesSectionExpanded(!miniTilesSectionExpanded)
              }
              sx={{
                fontSize: "12px",
                fontWeight: "500",
                color: "primary.500",
              }}
              trackingName={trackingLabel}
              trackingUniqueId={
                miniTilesSectionExpanded ? "show_less" : "show_more"
              }
            >
              {miniTilesSectionExpanded ? "Show less" : "Show more"}
            </TrackedButton>
          </Box>
        )}
      {loading ? (
        <Box
          flex
          sx={{
            justifyContent: "center",
            alignItems: "center",
            height: "80%",
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gridGap: "16px 32px",
            gap: "16px 32px",
            justifyContent: "space-between",
          }}
        >
          {tileListings?.map((listing: any, listingIndex: number) => (
            <MiniTilesListing
              key={listing.activity.uid}
              listing={listing}
              listingIndex={listingIndex}
              trackingLabel={trackingLabel}
            />
          ))}
        </Box>
      )}
    </PageWidthSection>
  );
}

function MiniTilesListing({
  listing,
  listingIndex,
  trackingLabel,
}: {
  listing: any;
  listingIndex: number;
  trackingLabel: string;
}) {
  const trackProductClicked = useProductClicked();
  const Link = useLinkComponent();
  return (
    <Link
      to={createPathWithActivity(listing.activity)}
      trackingName={trackingLabel}
      trackingUniqueId={listing.activity.uid}
      useLinkTracking
      trackViews={true}
      onClick={() => trackProductClicked(listing, listingIndex)}
    >
      <Listing
        listing={listing}
        showSaved={true}
        alignText={"start"}
        showFrequency={false}
      />
    </Link>
  );
}
